import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import Select from "react-select";
import { FaTrash, FaPlus, FaEye, FaTimes } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import { dslipYearsToDate } from "./utils/dSlip_years";
import { numericInputProps } from "./utils/numericInput";
import { PAYSLIP_FONTS, DEFAULT_FONT_KEY, fontCssStyle } from "./utils/payslipFonts";
import PayslipPreviewBody from "./PayslipPreviewBody";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Payslip_Generate.css";

// Cap per-component amount to guard against accidental extra-digit typos
// (matches Salary Structure's MAX_AMOUNT).
const MAX_AMOUNT = 99999999;

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const now = new Date();
// Default to last month
const DEFAULT_MONTH = now.getMonth() === 0 ? 12 : now.getMonth();
const DEFAULT_YEAR = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

// 2018 .. current year, newest first. No future years - you can't generate a
// payslip for a month that hasn't happened yet.
const YEAR_OPTIONS = dslipYearsToDate();

// Shared react-select styling so Month/Year dropdowns match the look used
// in Employee_Center / Payslip_Management (rounded card, slate border,
// orange focus ring). menuPortal lifts the menu above sibling cards.
const PG_MONTH_OPTIONS = MONTH_NAMES.slice(1).map((m, i) => ({ value: i + 1, label: m }));
const PG_YEAR_OPTIONS = YEAR_OPTIONS.map(y => ({ value: y, label: String(y) }));
const PG_SELECT_STYLES = {
    control: (p, s) => ({
        ...p,
        minHeight: 40,
        borderRadius: 8,
        borderColor: s.isFocused ? "#fb923c" : "#cbd5e1",
        boxShadow: s.isFocused ? "0 0 0 3px rgba(251, 146, 60, 0.15)" : "none",
        "&:hover": { borderColor: "#fb923c" },
    }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#4a90e2" : s.isFocused ? "#f3f4f6" : "#fff",
        color: s.isSelected ? "#fff" : "#0d1b2a",
        padding: "10px 12px",
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
};
const PG_MONTH_STYLES = { ...PG_SELECT_STYLES, container: p => ({ ...p, width: 170 }) };
const PG_YEAR_STYLES  = { ...PG_SELECT_STYLES, container: p => ({ ...p, width: 130 }) };

// ─── Calculation helper - mirrors backend exactly (dynamic component model) ────
//
// Gross         = Σ(Earning items)        + bonus + other_earnings
// Total Ded.    = Σ(Deduction items)      + lop_deduction + loan_deduction + other_deductions
// Net Pay       = Gross − Total Ded.
//
// LOP suggestion: when admin hasn't entered a value, suggest (Basic / days_in_month) × lop_days,
// where "Basic" is the structure component literally named "Basic".

function calcPayslip(structure, adj, year, month, editable) {
    const daysInMonth = new Date(year, month, 0).getDate();
    const lopDays = parseFloat(adj.lop_days) || 0;
    const daysWorked = daysInMonth - lopDays;

    // Source of truth for line items: the editable per-emp arrays when present
    // (admin-overridden for this payslip), else fall back to the salary
    // structure's frozen items so first-render before edits still shows data.
    const editEarn = Array.isArray(editable?.earnings) ? editable.earnings : null;
    const editDed = Array.isArray(editable?.deductions) ? editable.deductions : null;
    const items = Array.isArray(structure?.items) ? structure.items : [];
    const earnings = editEarn ?? items.filter((i) => i.component_type === "Earning");
    const deductions = editDed ?? items.filter((i) => i.component_type === "Deduction");

    const grossStructure = earnings.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const dedStructure = deductions.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

    const bonus = parseFloat(adj.bonus) || 0;
    const otherEarnings = parseFloat(adj.other_earnings) || 0;
    const loanDed = parseFloat(adj.loan_deduction) || 0;
    const otherDed = parseFloat(adj.other_deductions) || 0;

    // Auto-LOP suggestion (admin override wins)
    const basicItem = earnings.find((i) => i.component_name === "Basic");
    const basicAmount = basicItem ? parseFloat(basicItem.amount) || 0 : 0;
    const lopSuggested = lopDays > 0
        ? parseFloat(((basicAmount / daysInMonth) * lopDays).toFixed(2))
        : 0;
    const lopDed = (adj.lop_deduction !== undefined && adj.lop_deduction !== "" && adj.lop_deduction !== null)
        ? (parseFloat(adj.lop_deduction) || 0)
        : lopSuggested;

    const gross = grossStructure + bonus + otherEarnings;
    const totalDed = dedStructure + lopDed + loanDed + otherDed;
    const netPay = gross - totalDed;

    return {
        daysInMonth, lopDays, daysWorked,
        earnings, deductions,
        gross, totalDed, netPay,
        lopDed, lopSuggested, loanDed, otherDed,
        bonus, otherEarnings,
    };
}

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Step indicator ───────────────────────────────────────────────────────────

const STEPS = [
    { n: 1, label: "Select month" },
    { n: 2, label: "Select employees" },
    { n: 3, label: "Adjustments" },
    { n: 4, label: "Review & generate" },
];

function StepBar({ current }) {
    return (
        <div className="pg-stepbar">
            {STEPS.map((s, i) => (
                <React.Fragment key={s.n}>
                    <div className={`pg-step ${current === s.n ? "active" : current > s.n ? "done" : ""}`}>
                        <div className="pg-step-circle">{current > s.n ? "✓" : s.n}</div>
                        <div className="pg-step-label">{s.label}</div>
                    </div>
                    {i < STEPS.length - 1 && (
                        <div className={`pg-step-line ${current > s.n ? "done" : ""}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function Payslip_Generate({ navSize }) {
    const navigate = useNavigate();

    // Step
    const [step, setStep] = useState(1);

    // Step 1 - month/year
    const [year, setYear] = useState(DEFAULT_YEAR);
    const [month, setMonth] = useState(DEFAULT_MONTH);
    const [validating, setValidating] = useState(false);

    // Step 2 - employees
    const [eligible, setEligible] = useState([]);
    const [noStructure, setNoStructure] = useState([]);
    const [selected, setSelected] = useState({}); // { emp_id: true/false }
    const [loadingEmps, setLoadingEmps] = useState(false);

    // Step 3 - adjustments per employee
    // { emp_id: { lop_days, bonus, loan_deduction, other_earnings, other_earnings_note,
    //            other_deductions, other_deductions_note, company_address } }
    const [adjustments, setAdj] = useState({});

    // Step 3 - which selected employee is currently being edited. Tab strip
    // and prev/next arrows drive this. Reset to 0 whenever the user lands
    // on step 3 fresh so they always start at the first employee.
    const [currentEmpIndex, setCurrentEmpIndex] = useState(0);

    // Step 3 - per-emp editable structure items. Same model as Salary Structure:
    //   { emp_id: { earnings: [{component_id, amount}, ...], deductions: [...] } }
    // Initialised from emp.structure on first visit. Admin can edit/add/remove
    // rows just like the SS modal - these become the snapshotted items on the
    // generated payslip, overriding the salary structure for THIS payslip only.
    const [editItems, setEditItems] = useState({});

    // Component options for the dropdown - same endpoint Payslip_Salary_Structure uses.
    const [earnOptions, setEarnOptions] = useState([]);
    const [dedOptions, setDedOptions] = useState([]);

    // Step 4
    const [generating, setGenerating] = useState(false);
    const [result, setResult] = useState(null); // { batch_number, created_count }

    // Step 4 - which employee's payslip is being previewed (null = closed).
    // The preview renders the EXACT PDF layout (shared PayslipPreviewBody)
    // from the in-memory structure + adjustments, before the draft exists.
    const [previewEmpId, setPreviewEmpId] = useState(null);

    // Load component options once on mount so the dropdowns are ready by step 3.
    useEffect(() => {
        apiFetch("/api/payslip/components")
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setEarnOptions(list.filter((c) => c.component_type === "Earning"));
                setDedOptions(list.filter((c) => c.component_type === "Deduction"));
            })
            .catch((err) => console.error("❌ load components error:", err));
    }, []);

    // Per-emp editable-items helpers ─────────────────────────
    // Lazy-init from emp.structure the first time we touch an emp.
    const ensureEditItems = useCallback((emp) => {
        setEditItems((prev) => {
            if (prev[emp.emp_id]) return prev;
            const items = Array.isArray(emp.structure?.items) ? emp.structure.items : [];
            return {
                ...prev,
                [emp.emp_id]: {
                    earnings: items
                        .filter((i) => i.component_type === "Earning")
                        .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") })),
                    deductions: items
                        .filter((i) => i.component_type === "Deduction")
                        .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") })),
                },
            };
        });
    }, []);

    const setEditRow = (empId, type, idx, patch) => {
        setEditItems((prev) => {
            const key = type === "Earning" ? "earnings" : "deductions";
            const list = prev[empId]?.[key] || [];
            return {
                ...prev,
                [empId]: {
                    ...prev[empId],
                    [key]: list.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
                },
            };
        });
    };
    const addEditRow = (empId, type) => {
        setEditItems((prev) => {
            const key = type === "Earning" ? "earnings" : "deductions";
            const list = prev[empId]?.[key] || [];
            return {
                ...prev,
                [empId]: { ...prev[empId], [key]: [...list, { component_id: "", amount: "" }] },
            };
        });
    };
    const removeEditRow = (empId, type, idx) => {
        setEditItems((prev) => {
            const key = type === "Earning" ? "earnings" : "deductions";
            const list = prev[empId]?.[key] || [];
            return {
                ...prev,
                [empId]: { ...prev[empId], [key]: list.filter((_, i) => i !== idx) },
            };
        });
    };

    // Hide options already chosen elsewhere in the same column
    const availableOptions = (allOptions, rows, currentIdx) => {
        const chosen = rows.map((r, i) => (i === currentIdx ? null : r.component_id)).filter(Boolean);
        return allOptions.filter((o) => !chosen.includes(String(o.component_id)));
    };

    // Resolve component_id → name (used by calc to attach a display label for the
    // editable rows; the option list is the source of truth for the name).
    const componentName = useCallback((id, type) => {
        const list = type === "Earning" ? earnOptions : dedOptions;
        const c = list.find((o) => String(o.component_id) === String(id));
        return c?.component_name || "";
    }, [earnOptions, dedOptions]);

    // Build the normalized payslip `data` object for the shared PDF preview,
    // straight from the in-memory salary structure + per-emp adjustments +
    // edited component rows. Mirrors exactly what create_drafts will snapshot,
    // so the preview matches the generated payslip 1:1.
    const buildPreviewData = useCallback((emp) => {
        if (!emp) return null;
        const adj = adjustments[emp.emp_id] || {};
        const editable = editItems[emp.emp_id];
        const calc = calcPayslip(emp.structure, adj, year, month, editable);

        // Line items with resolved component names. Prefer the admin-edited
        // rows; fall back to the frozen structure items when not yet touched.
        let items;
        if (editable) {
            items = [
                ...editable.earnings
                    .filter((r) => r.component_id)
                    .map((r) => ({
                        component_type: "Earning",
                        component_name: componentName(r.component_id, "Earning"),
                        amount: parseFloat(r.amount) || 0,
                    })),
                ...editable.deductions
                    .filter((r) => r.component_id)
                    .map((r) => ({
                        component_type: "Deduction",
                        component_name: componentName(r.component_id, "Deduction"),
                        amount: parseFloat(r.amount) || 0,
                    })),
            ];
        } else {
            items = (emp.structure?.items || []).map((i) => ({
                component_type: i.component_type,
                component_name: i.component_name,
                amount: parseFloat(i.amount) || 0,
            }));
        }

        return {
            // Identity (from the eligible-employee record)
            emp_id: emp.emp_id,
            emp_first_name: emp.emp_first_name,
            emp_last_name: emp.emp_last_name,
            emp_code: emp.emp_id,
            pan_number: emp.pan_number,
            emp_location: emp.emp_location,
            job_position: emp.job_position,
            emp_department: emp.emp_department,
            bank_account_number: emp.bank_account_number,
            doj: emp.doj,
            // Period
            pay_month: month,
            pay_year: year,
            days_in_month: calc.daysInMonth,
            lop_days: calc.lopDays,
            // Line items + one-off adjustments
            items,
            bonus: calc.bonus,
            other_earnings: calc.otherEarnings,
            other_earnings_note: adj.other_earnings_note,
            lop_deduction: calc.lopDed,
            loan_deduction: calc.loanDed,
            other_deductions: calc.otherDed,
            other_deductions_note: adj.other_deductions_note,
            // Totals
            gross_salary: calc.gross,
            total_deductions: calc.totalDed,
            net_pay: calc.netPay,
            // Presentation
            company_address: adj.company_address !== undefined
                ? adj.company_address
                : (emp.structure?.company_address || ""),
            font: adj.font || emp.structure?.font || DEFAULT_FONT_KEY,
        };
    }, [adjustments, editItems, componentName, year, month]);

    // ── Auth ──
    useEffect(() => {
        apiFetch("/api/employee/logined_employee", { method: "GET", credentials: "include" })
            .then(r => r.json())
            .then(data => {
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            })
            .catch(() => navigate("/login"));
    }, [navigate]);

    // ── Helpers ──
    const getAdj = (empId, key) => adjustments[empId]?.[key] ?? "";
    const setAdjField = (empId, key, val) => {
        setAdj(prev => ({
            ...prev,
            [empId]: { ...prev[empId], [key]: val },
        }));
    };

    // Update LOP Days AND auto-fill LOP Deduction with (Basic / days_in_month × lop_days).
    // Admin can still type a custom value into LOP Deduction afterwards - the auto-fill
    // is just a sensible starting point that follows the LOP rule. Setting LOP Days to
    // 0 clears LOP Deduction back to "".
    const setLopDays = (emp, newLopDays) => {
        setAdj(prev => {
            const days = parseFloat(newLopDays) || 0;
            const basicItem = (emp.structure?.items || []).find(i => i.component_name === "Basic");
            const basic = basicItem ? parseFloat(basicItem.amount) || 0 : 0;
            const daysInMonth = new Date(year, month, 0).getDate();
            const lopDed = days > 0 && basic > 0
                ? parseFloat(((basic / daysInMonth) * days).toFixed(2))
                : 0;
            return {
                ...prev,
                [emp.emp_id]: {
                    ...prev[emp.emp_id],
                    lop_days: newLopDays,
                    lop_deduction: days > 0 ? String(lopDed) : "",
                },
            };
        });
    };

    const selectedEmps = eligible.filter(e => selected[e.emp_id] && !e.alreadyGenerated && !e.blocked_sequential);

    // Pre-flight check for Step 3 → Step 4. The auto-fill normally keeps
    // LOP Deduction in sync with LOP Days, but the admin can clear it
    // manually - in which case there's an inconsistency to surface
    // (LOP days exist but no deduction is recorded). Returns null when
    // everything is OK, otherwise { empId, empName, message }.
    const validateAdjustments = () => {
        for (const emp of selectedEmps) {
            const adj = adjustments[emp.emp_id] || {};
            const editable = editItems[emp.emp_id];

            const empName = `${emp.emp_first_name || ""} ${emp.emp_last_name || ""}`.trim() || emp.emp_id;
            const fail = (message) => ({ empId: emp.emp_id, empName, message });

            // ── LOP consistency (existing rule) ──
            const lopDays = parseFloat(adj.lop_days) || 0;
            const lopDedRaw = adj.lop_deduction;
            const lopDedEmpty = lopDedRaw === "" || lopDedRaw === undefined || lopDedRaw === null;
            if (lopDays > 0 && lopDedEmpty) {
                return fail(`LOP Days is set to <strong>${lopDays}</strong> but LOP Deduction is empty. Enter a deduction amount, or clear LOP Days if no deduction applies.`);
            }

            // ── Structure-item rules (mirror Payslip_Salary_Structure) ──
            if (editable) {
                const earnRows = editable.earnings.filter((r) => r.component_id);
                const dedRows = editable.deductions.filter((r) => r.component_id);

                if (earnRows.length === 0) {
                    return fail("Add at least one Earning component in the structure table.");
                }
                if (earnRows.some((r) => r.amount === "" || r.amount == null || isNaN(parseFloat(r.amount)))) {
                    return fail("Every Earning component must have an amount (0 or more).");
                }
                if (dedRows.some((r) => r.amount === "" || r.amount == null || isNaN(parseFloat(r.amount)))) {
                    return fail("Every Deduction component must have an amount (0 or more).");
                }

                // Sanity cap per component amount
                const overflow = [...earnRows, ...dedRows].some((r) => {
                    const n = parseFloat(r.amount);
                    return !isNaN(n) && n > MAX_AMOUNT;
                });
                if (overflow) {
                    return fail(`Amount(s) exceed the maximum of ₹${MAX_AMOUNT.toLocaleString("en-IN")} per item.`);
                }

                // Net Pay must be strictly positive (matches SS rule)
                const calc = calcPayslip(emp.structure, adj, year, month, editable);
                if (calc.netPay <= 0) {
                    return fail(`Net Pay is <strong>₹${fmt(calc.netPay)}</strong>. Earnings must exceed total deductions - review the structure or adjustments.`);
                }
            }
        }
        return null;
    };

    // ─────────────────────────────────────────────────────────
    //  STEP 1 - Validate month & proceed
    // ─────────────────────────────────────────────────────────
    const handleValidateMonth = async () => {
        setValidating(true);
        try {
            const res = await apiFetch(`/api/payslip/generate/validate_month?year=${year}&month=${month}`);
            const data = await res.json();
            if (!data.valid) {
                dnAlert.warning("Cannot proceed", data.reason);
                return;
            }
            // Valid - load employees
            await loadEmployees();
            setStep(2);
        } catch {
            dnAlert.error("Error", "Failed to validate month. Please try again.");
        } finally {
            setValidating(false);
        }
    };

    // ─────────────────────────────────────────────────────────
    //  STEP 2 - Load employees
    // ─────────────────────────────────────────────────────────
    const loadEmployees = async () => {
        setLoadingEmps(true);
        // Clear any per-emp adjustments / item edits from a prior month -
        // re-using them here would mean April's Step 3 renders March's
        // edited components instead of pulling fresh from the structure
        // that's active for April.
        setAdj({});
        setEditItems({});
        setCurrentEmpIndex(0);
        try {
            const res = await apiFetch(`/api/payslip/generate/employees?year=${year}&month=${month}`);
            const data = await res.json();
            setEligible(data.eligible || []);
            setNoStructure(data.noStructure || []);
            // Pre-select all that haven't been generated yet
            const sel = {};
            (data.eligible || []).forEach(e => {
                if (!e.alreadyGenerated) sel[e.emp_id] = true;
            });
            setSelected(sel);

            // Pre-fill LOP days from dTime attendance (reconciled rows only).
            // Admin can still override the number in Step 3; lop_deduction is
            // computed exactly as the manual flow does (Basic / days_in_month × lop).
            const daysInMonth = new Date(year, month, 0).getDate();
            const initAdj = {};
            (data.eligible || []).forEach(e => {
                if (e.alreadyGenerated || e.blocked_sequential) return;
                if (!e.attendance || !e.attendance.reconciles) return;
                const lop = parseFloat(e.lop_days) || 0;
                const basicItem = (e.structure?.items || []).find(i => i.component_name === "Basic");
                const basic = basicItem ? parseFloat(basicItem.amount) || 0 : 0;
                const lopDed = lop > 0 && basic > 0
                    ? parseFloat(((basic / daysInMonth) * lop).toFixed(2)) : 0;
                initAdj[e.emp_id] = {
                    lop_days: String(lop),
                    lop_deduction: lop > 0 ? String(lopDed) : "",
                    lop_prefilled: true,
                };
            });
            setAdj(initAdj);
        } catch {
            dnAlert.error("Error", "Failed to load employees.");
        } finally {
            setLoadingEmps(false);
        }
    };

    const toggleAll = () => {
        const selectable = eligible.filter(e => !e.alreadyGenerated && !e.blocked_sequential);
        const allOn = selectable.every(e => selected[e.emp_id]);
        const next = {};
        selectable.forEach(e => { next[e.emp_id] = !allOn; });
        setSelected(next);
    };

    const toggleOne = (empId) => {
        setSelected(prev => ({ ...prev, [empId]: !prev[empId] }));
    };

    const handleProceedToAdjustments = () => {
        if (selectedEmps.length === 0) {
            dnAlert.warning("No employees selected", "Please select at least one employee to generate payslips.");
            return;
        }
        setCurrentEmpIndex(0); // always start at the first employee
        setStep(3);
    };

    // ─────────────────────────────────────────────────────────
    //  STEP 4 - Generate drafts
    // ─────────────────────────────────────────────────────────
    const handleGenerate = async () => {
        // Defence-in-depth - should already have been caught by the
        // "Review & Generate →" button on step 3, but in case the user
        // somehow lands on step 4 with an invalid LOP setup (e.g. they
        // clear the field after navigating), block here too.
        const v = validateAdjustments();
        if (v) {
            dnAlert.warning("Missing LOP Deduction", `${v.empName} - ${v.message}`);
            setStep(3);
            return;
        }
        setGenerating(true);
        try {
            const payslips = selectedEmps.map(emp => {
                const rawLopDed = getAdj(emp.emp_id, "lop_deduction");
                return {
                    emp_id: emp.emp_id,
                    lop_days: parseFloat(getAdj(emp.emp_id, "lop_days")) || 0,
                    // Pass empty when admin didn't override → backend auto-computes the suggestion
                    lop_deduction: rawLopDed === "" || rawLopDed === undefined || rawLopDed === null ? "" : parseFloat(rawLopDed) || 0,
                    bonus: parseFloat(getAdj(emp.emp_id, "bonus")) || 0,
                    loan_deduction: parseFloat(getAdj(emp.emp_id, "loan_deduction")) || 0,
                    other_earnings: parseFloat(getAdj(emp.emp_id, "other_earnings")) || 0,
                    other_earnings_note: getAdj(emp.emp_id, "other_earnings_note"),
                    other_deductions: parseFloat(getAdj(emp.emp_id, "other_deductions")) || 0,
                    other_deductions_note: getAdj(emp.emp_id, "other_deductions_note"),
                    // Per-emp override of Company Address. Empty/whitespace means
                    // "use whatever the structure already has" → backend keeps
                    // structure.company_address as the snapshot.
                    company_address: (getAdj(emp.emp_id, "company_address") || "").toString(),
                    // Per-emp font override. Empty means "inherit from structure"
                    // - backend resolves it. Frontend already pre-fills from
                    // emp.structure.font so admin always sees the current pick.
                    font: getAdj(emp.emp_id, "font") || emp.structure?.font || DEFAULT_FONT_KEY,
                    // Per-emp overridden structure items (admin can edit Basic/HRA/PF/etc.
                    // or add/remove components for THIS payslip only). Backend uses these
                    // if present; otherwise it falls back to the salary structure.
                    items: (() => {
                        const ed = editItems[emp.emp_id];
                        if (!ed) return null;
                        return [
                            ...ed.earnings
                                .filter((r) => r.component_id && !isNaN(parseFloat(r.amount)))
                                .map((r) => ({ component_id: parseInt(r.component_id), amount: parseFloat(r.amount), component_type: "Earning" })),
                            ...ed.deductions
                                .filter((r) => r.component_id && !isNaN(parseFloat(r.amount)))
                                .map((r) => ({ component_id: parseInt(r.component_id), amount: parseFloat(r.amount), component_type: "Deduction" })),
                        ];
                    })(),
                };
            });

            const res = await apiFetch("/api/payslip/generate/create_drafts", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ year, month, payslips }),
            });
            const data = await res.json();

            if (data.success) {
                setResult(data);
                setStep(4);
            } else {
                dnAlert.error("Error", data.error || "Failed to generate payslips.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        } finally {
            setGenerating(false);
        }
    };

    const handleStartNew = () => {
        setStep(1);
        setEligible([]);
        setNoStructure([]);
        setSelected({});
        setAdj({});
        // Wipe per-emp item edits from the previous generation; otherwise
        // editItems[emp_id] survives across months and the next month's
        // Step 3 ends up rendering the OLD month's overridden components
        // instead of pulling fresh from emp.structure.
        setEditItems({});
        setCurrentEmpIndex(0);
        setResult(null);
    };

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    return (
        <div className="pg-container">
            <div className={`pg-dashboard ${navSize}`}>
                <div className="pg-content">

                    {/* ── Page header ── */}
                    <div className="pg-page-header">
                        <div className="pg-page-header-left">
                            <h2>
                                <a className="navbar-brand" href="#">
                                    <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px", marginRight: "10px" }} />
                                </a>
                                Generate Payslips
                            </h2>
                            <p className="pg-subtitle">
                                Bulk-create draft payslips for a pay month. Review and publish from Payslip Management.
                            </p>
                        </div>
                    </div>

                    {/* ── Step bar ── */}
                    <StepBar current={step} />

                    {/* ── Step panels ── */}
                    <div className="pg-card">

                        {/* ══ STEP 1 - SELECT MONTH ══ */}
                        {step === 1 && (
                            <div className="pg-step-panel">
                                <div className="pg-step-title">Select Pay Month & Year</div>
                                <p className="pg-step-desc">
                                    Choose the month you want to generate payslips for. Future months and months
                                    with missing prior-month payslips will be blocked.
                                </p>

                                <div className="pg-month-selectors">
                                    <div className="pg-field-group">
                                        <label>Month</label>
                                        <Select
                                            options={PG_MONTH_OPTIONS}
                                            value={PG_MONTH_OPTIONS.find(o => o.value === month)}
                                            onChange={opt => setMonth(opt.value)}
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={PG_MONTH_STYLES}
                                        />
                                    </div>
                                    <div className="pg-field-group">
                                        <label>Year</label>
                                        <Select
                                            options={PG_YEAR_OPTIONS}
                                            value={PG_YEAR_OPTIONS.find(o => o.value === year)}
                                            onChange={opt => setYear(opt.value)}
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={PG_YEAR_STYLES}
                                        />
                                    </div>
                                </div>

                                <div className="pg-info-box">
                                    <span className="pg-info-icon">ℹ</span>
                                    Generating payslips for <strong>{MONTH_NAMES[month]} {year}</strong>.
                                    The system will verify this is not a future month and that the previous
                                    month's payslips already exist.
                                </div>

                                <div className="pg-step-footer">
                                    <button
                                        className="pg-btn-primary"
                                        onClick={handleValidateMonth}
                                        disabled={validating}
                                    >
                                        {validating ? "Validating…" : "Continue →"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ══ STEP 2 - SELECT EMPLOYEES ══ */}
                        {step === 2 && (
                            <div className="pg-step-panel">
                                <div className="pg-step-title">
                                    Select Employees - {MONTH_NAMES[month]} {year}
                                </div>

                                {loadingEmps ? (
                                    <div className="pg-loading">Loading employees…</div>
                                ) : (
                                    <>
                                        {/* No structure warning */}
                                        {noStructure.length > 0 && (
                                            <div className="pg-warning-box">
                                                <strong>⚠ {noStructure.length} employee{noStructure.length > 1 ? "s" : ""} excluded</strong> - no active salary structure configured:&nbsp;
                                                {noStructure.map(e => `${e.emp_first_name} ${e.emp_last_name}`).join(", ")}.
                                                Configure their structures in <em>Salary Structure</em> first.
                                            </div>
                                        )}

                                        {/* Blocked warning - reason shown inline for each employee */}
                                        {eligible.filter(e => e.blocked_sequential).length > 0 && (
                                            <div className="pg-warning-box" style={{ marginBottom: 12 }}>
                                                <strong>⚠ {eligible.filter(e => e.blocked_sequential).length} employee{eligible.filter(e => e.blocked_sequential).length > 1 ? "s" : ""} blocked from generation:</strong>
                                                <ul style={{ margin: "6px 0 0 20px", padding: 0 }}>
                                                    {eligible.filter(e => e.blocked_sequential).map(e => (
                                                        <li key={e.emp_id} style={{ marginTop: 2 }}>
                                                            <strong>{e.emp_first_name} {e.emp_last_name}</strong> ({e.emp_id}) - {e.blocked_reason}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Summary counts */}
                                        <div className="pg-emp-summary">
                                            <div className="pg-emp-stat">
                                                <span className="pg-emp-stat-val">{eligible.filter(e => !e.alreadyGenerated && !e.blocked_sequential).length}</span>
                                                <span className="pg-emp-stat-lbl">Eligible</span>
                                            </div>
                                            <div className="pg-emp-stat">
                                                <span className="pg-emp-stat-val">{eligible.filter(e => e.alreadyGenerated).length}</span>
                                                <span className="pg-emp-stat-lbl">Already generated</span>
                                            </div>
                                            <div className="pg-emp-stat" style={{ color: '#e65100' }}>
                                                <span className="pg-emp-stat-val">{eligible.filter(e => e.blocked_sequential).length}</span>
                                                <span className="pg-emp-stat-lbl">Blocked</span>
                                            </div>
                                            <div className="pg-emp-stat pg-emp-stat--blue">
                                                <span className="pg-emp-stat-val">{selectedEmps.length}</span>
                                                <span className="pg-emp-stat-lbl">Selected</span>
                                            </div>
                                        </div>

                                        {/* Employee table */}
                                        <div className="pg-table-wrapper">
                                            <table className="pg-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 44 }}>
                                                            <input
                                                                type="checkbox"
                                                                className="pg-checkbox"
                                                                checked={
                                                                    eligible.filter(e => !e.alreadyGenerated && !e.blocked_sequential).length > 0 &&
                                                                    eligible.filter(e => !e.alreadyGenerated && !e.blocked_sequential).every(e => selected[e.emp_id])
                                                                }
                                                                onChange={toggleAll}
                                                            />
                                                        </th>
                                                        <th>Employee</th>
                                                        <th>Department</th>
                                                        <th>Gross</th>
                                                        <th>Net Pay</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {eligible.length === 0 ? (
                                                        <tr><td colSpan={6} className="pg-empty">No eligible employees found.</td></tr>
                                                    ) : (
                                                        eligible.map(emp => (
                                                            <tr
                                                                key={emp.emp_id}
                                                                className={emp.alreadyGenerated ? "pg-row-dimmed" : ""}
                                                                onClick={() => !emp.alreadyGenerated && !emp.blocked_sequential && toggleOne(emp.emp_id)}
                                                                style={{ cursor: (emp.alreadyGenerated || emp.blocked_sequential) ? "default" : "pointer" }}
                                                            >
                                                                <td onClick={e => e.stopPropagation()}>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="pg-checkbox"
                                                                        checked={!!selected[emp.emp_id]}
                                                                        disabled={emp.alreadyGenerated || emp.blocked_sequential}
                                                                        onChange={() => toggleOne(emp.emp_id)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div className="pg-emp-cell">
                                                                        <div className="pg-avatar">{emp.emp_first_name?.[0]?.toUpperCase()}</div>
                                                                        <div>
                                                                            <div className="pg-emp-name">{emp.emp_first_name} {emp.emp_last_name}</div>
                                                                            <div className="pg-emp-id">{emp.emp_id}</div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>{emp.emp_department || "-"}</td>
                                                                <td className="pg-num">₹{fmt(emp.structure?.gross)}</td>
                                                                <td className="pg-num">₹{fmt(emp.structure?.net_pay)}</td>
                                                                <td>
                                                                    {emp.alreadyGenerated
                                                                        ? <span className="pg-pill pill-green">Already generated</span>
                                                                        : emp.blocked_sequential
                                                                            ? <span className="pg-pill pill-orange" title={emp.blocked_reason}>⚠ Blocked</span>
                                                                            : selected[emp.emp_id]
                                                                                ? <span className="pg-pill pill-blue">Selected</span>
                                                                                : <span className="pg-pill pill-grey">Skipped</span>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="pg-step-footer">
                                            <button className="pg-btn-secondary" onClick={() => setStep(1)}>← Back</button>
                                            <button
                                                className="pg-btn-primary"
                                                onClick={handleProceedToAdjustments}
                                                disabled={selectedEmps.length === 0}
                                            >
                                                Continue with {selectedEmps.length} employee{selectedEmps.length !== 1 ? "s" : ""} →
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* ══ STEP 3 - ADJUSTMENTS ══ */}
                        {step === 3 && (
                            <div className="pg-step-panel">
                                <div className="pg-step-title">
                                    Enter Adjustments - {MONTH_NAMES[month]} {year}
                                </div>
                                <p className="pg-step-desc">
                                    Enter LOP days, bonus, and any one-off earnings or deductions for each employee.
                                    Earning &amp; Deduction components come from each employee's salary structure.
                                    LOP Deduction auto-suggests from Basic × LOP days - edit it to override.
                                </p>

                                {/* Employee tab strip - click any chip to jump to that employee.
                                    Currently-active chip is highlighted; the rest stay visible
                                    so the admin can see the full roster without scrolling. */}
                                {selectedEmps.length > 0 && (
                                    <div className="pg-adj-tabs">
                                        {selectedEmps.map((emp, i) => (
                                            <button
                                                key={emp.emp_id}
                                                type="button"
                                                className={`pg-adj-tab ${i === currentEmpIndex ? "pg-adj-tab--active" : ""}`}
                                                onClick={() => setCurrentEmpIndex(i)}
                                                title={`${emp.emp_first_name} ${emp.emp_last_name} · ${emp.emp_id}`}
                                            >
                                                <span className="pg-adj-tab-num">{i + 1}</span>
                                                <span className="pg-adj-tab-name">
                                                    {emp.emp_first_name} {emp.emp_last_name}
                                                </span>
                                                <span className="pg-adj-tab-id">{emp.emp_id}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Single-employee view with side arrows for prev/next */}
                                <div className="pg-adj-pager">
                                    <button
                                        type="button"
                                        className="pg-adj-arrow pg-adj-arrow--prev"
                                        onClick={() => setCurrentEmpIndex((i) => Math.max(0, i - 1))}
                                        disabled={currentEmpIndex === 0}
                                        title="Previous employee"
                                    >‹</button>

                                    <div className="pg-adj-single">
                                        {(() => {
                                            const emp = selectedEmps[currentEmpIndex];
                                            if (!emp) return null;
                                            // Lazy-init editable items for this emp on first view.
                                            // Effect-style call inside render is safe here because
                                            // setEditItems is idempotent when the emp's slot exists.
                                            if (!editItems[emp.emp_id]) ensureEditItems(emp);
                                            const adj = adjustments[emp.emp_id] || {};
                                            const editable = editItems[emp.emp_id];
                                            const calc = calcPayslip(emp.structure, adj, year, month, editable);
                                            return (
                                                <>
                                                    <div className="pg-adj-card" key={emp.emp_id}>
                                                        {/* Card header */}
                                                        <div className="pg-adj-card-header">
                                                            <div className="pg-adj-emp-info">
                                                                <div className="pg-avatar pg-avatar--sm">{emp.emp_first_name?.[0]?.toUpperCase()}</div>
                                                                <div>
                                                                    <div className="pg-emp-name">{emp.emp_first_name} {emp.emp_last_name}</div>
                                                                    <div className="pg-emp-id">{emp.emp_id} · {emp.emp_department}</div>
                                                                </div>
                                                            </div>
                                                            <div className="pg-adj-net-preview">
                                                                <span className="pg-adj-net-label">Est. Net Pay</span>
                                                                <span className="pg-adj-net-val">₹{fmt(calc.netPay)}</span>
                                                            </div>
                                                        </div>

                                                        {/* Adjustments - split into Earnings (left) + Deductions (right)
                                                    sections so LOP / Loan / Other Deductions are grouped where
                                                    they semantically belong. Mirrors the salary structure
                                                    preview's earnings/deductions colour language. */}
                                                        <div className="pg-adj-sections">
                                                            {/* ── EARNINGS column ── */}
                                                            <div className="pg-adj-section pg-adj-section--earn">
                                                                <div className="pg-adj-section-head">
                                                                    <span>Earnings</span>
                                                                    <span className="pg-adj-section-sub">one-off additions</span>
                                                                </div>
                                                                <div className="pg-adj-section-body">
                                                                    <div className="pg-adj-field">
                                                                        <label>Bonus (₹)</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="pg-input"
                                                                            placeholder="0"
                                                                            value={getAdj(emp.emp_id, "bonus")}
                                                                            onChange={e => setAdjField(emp.emp_id, "bonus", e.target.value)}
                                                                            {...numericInputProps}
                                                                        />
                                                                    </div>
                                                                    <div className="pg-adj-field">
                                                                        <label>Other Earnings (₹)</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="pg-input"
                                                                            placeholder="0"
                                                                            value={getAdj(emp.emp_id, "other_earnings")}
                                                                            onChange={e => setAdjField(emp.emp_id, "other_earnings", e.target.value)}
                                                                            {...numericInputProps}
                                                                        />
                                                                    </div>
                                                                    <div className="pg-adj-field">
                                                                        <label>Other Earnings Note</label>
                                                                        <input
                                                                            type="text"
                                                                            className="pg-input"
                                                                            placeholder="e.g. Festival bonus, Referral reward…"
                                                                            value={getAdj(emp.emp_id, "other_earnings_note")}
                                                                            onChange={e => setAdjField(emp.emp_id, "other_earnings_note", e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* ── DEDUCTIONS column ── */}
                                                            <div className="pg-adj-section pg-adj-section--ded">
                                                                <div className="pg-adj-section-head">
                                                                    <span>Deductions</span>
                                                                    <span className="pg-adj-section-sub">LOP & one-off recoveries</span>
                                                                </div>
                                                                <div className="pg-adj-section-body">
                                                                    <div className="pg-adj-row-pair">
                                                                        <div className="pg-adj-field">
                                                                            <label>LOP Days</label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                step="0.5"
                                                                                max={calc.daysInMonth}
                                                                                className="pg-input"
                                                                                placeholder="0"
                                                                                value={getAdj(emp.emp_id, "lop_days")}
                                                                                onChange={e => setLopDays(emp, e.target.value)}
                                                                                {...numericInputProps}
                                                                            />
                                                                        </div>
                                                                        <div className="pg-adj-field">
                                                                            <label>
                                                                                LOP Deduction (₹)
                                                                                {calc.lopDays > 0 && (
                                                                                    <span className="pg-hint pg-hint--auto">&nbsp;auto-filled</span>
                                                                                )}
                                                                            </label>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                className="pg-input"
                                                                                placeholder="0"
                                                                                value={getAdj(emp.emp_id, "lop_deduction")}
                                                                                onChange={e => setAdjField(emp.emp_id, "lop_deduction", e.target.value)}
                                                                                {...numericInputProps}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    {emp.attendance && (
                                                                        <div className="pg-adj-att">
                                                                            {emp.attendance.reconciles ? (
                                                                                <>
                                                                                    <div className="pg-adj-att-note">
                                                                                        <span className="pg-adj-att-dot" /> LOP prefilled from attendance
                                                                                        {emp.attendance.paid_leave_days > 0 ? ` · ${emp.attendance.paid_leave_days} paid leave` : ""}
                                                                                        {emp.attendance.unpaid_leave_days > 0 ? ` · ${emp.attendance.unpaid_leave_days} unpaid (LOP)` : ""}.
                                                                                    </div>
                                                                                    {emp.attendance.unmarked_absent_days > 0 && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pg-adj-att-add"
                                                                                            onClick={() => {
                                                                                                const cur = parseFloat(getAdj(emp.emp_id, "lop_days")) || 0;
                                                                                                setLopDays(emp, String(cur + emp.attendance.unmarked_absent_days));
                                                                                            }}
                                                                                        >
                                                                                            + Add {emp.attendance.unmarked_absent_days} unmarked-absent day{emp.attendance.unmarked_absent_days === 1 ? "" : "s"} to LOP
                                                                                        </button>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                <div className="pg-adj-att-warn">⚠ {emp.attendance.error || "Attendance unavailable"}</div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    <div className="pg-adj-field">
                                                                        <label>Loan / Advance Recovery (₹)</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="pg-input"
                                                                            placeholder="0"
                                                                            value={getAdj(emp.emp_id, "loan_deduction")}
                                                                            onChange={e => setAdjField(emp.emp_id, "loan_deduction", e.target.value)}
                                                                            {...numericInputProps}
                                                                        />
                                                                    </div>
                                                                    <div className="pg-adj-field">
                                                                        <label>Other Deductions (₹)</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="pg-input"
                                                                            placeholder="0"
                                                                            value={getAdj(emp.emp_id, "other_deductions")}
                                                                            onChange={e => setAdjField(emp.emp_id, "other_deductions", e.target.value)}
                                                                            {...numericInputProps}
                                                                        />
                                                                    </div>
                                                                    <div className="pg-adj-field">
                                                                        <label>Other Deductions Note</label>
                                                                        <input
                                                                            type="text"
                                                                            className="pg-input"
                                                                            placeholder="e.g. Equipment damage, Uniform…"
                                                                            value={getAdj(emp.emp_id, "other_deductions_note")}
                                                                            onChange={e => setAdjField(emp.emp_id, "other_deductions_note", e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Editable 4-column structure table - same shape as the
                                                    Salary Structure modal. Each row has a component dropdown
                                                    + amount input + remove button. Add Earning / Add Deduction
                                                    buttons append new rows. The one-off adjustments entered
                                                    above (Bonus, LOP, Loan, Other Earnings/Deductions) show
                                                    as read-only addendum rows AT THE BOTTOM with note-text
                                                    as the row label when provided. */}
                                                        {(() => {
                                                            const earnList = editable?.earnings || [];
                                                            const dedList = editable?.deductions || [];
                                                            // Read-only addendum rows from the one-off adjustments -
                                                            // use the admin's note when provided so "Festival 100.00"
                                                            // shows instead of the generic "Other Earnings 100.00".
                                                            const addEarn = [
                                                                ...(calc.bonus > 0 ? [{ label: "Bonus", amount: calc.bonus }] : []),
                                                                ...(calc.otherEarnings > 0 ? [{ label: (adj.other_earnings_note || "").trim() || "Other Earnings", amount: calc.otherEarnings }] : []),
                                                            ];
                                                            const addDed = [
                                                                ...(calc.lopDed > 0 ? [{ label: "LOP deduction", amount: calc.lopDed }] : []),
                                                                ...(calc.loanDed > 0 ? [{ label: "Loan / advance recovery", amount: calc.loanDed }] : []),
                                                                ...(calc.otherDed > 0 ? [{ label: (adj.other_deductions_note || "").trim() || "Other Deductions", amount: calc.otherDed }] : []),
                                                            ];
                                                            const maxStruct = Math.max(earnList.length, dedList.length, 1);
                                                            const maxAddon = Math.max(addEarn.length, addDed.length);

                                                            const renderEditCell = (type, rows, idx) => {
                                                                const row = rows[idx];
                                                                const opts = type === "Earning" ? earnOptions : dedOptions;
                                                                if (!row) {
                                                                    return (<><div className="pg-pay-cell" /><div className="pg-pay-cell" /></>);
                                                                }
                                                                const available = availableOptions(opts, rows, idx);
                                                                const compOpts = available.map((o) => ({ value: String(o.component_id), label: o.component_name }));
                                                                if (row.component_id && !compOpts.find((o) => o.value === String(row.component_id))) {
                                                                    const c = opts.find((o) => String(o.component_id) === String(row.component_id));
                                                                    if (c) compOpts.unshift({ value: String(c.component_id), label: c.component_name });
                                                                }
                                                                const current = compOpts.find((o) => o.value === String(row.component_id)) || null;
                                                                return (
                                                                    <>
                                                                        <div className="pg-pay-cell pg-pay-cell-select">
                                                                            <div className="pg-pay-rs-wrap">
                                                                                <Select
                                                                                    classNamePrefix="pg-rs"
                                                                                    options={compOpts}
                                                                                    value={current}
                                                                                    isSearchable
                                                                                    isClearable
                                                                                    placeholder="- select -"
                                                                                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                                                    menuPlacement="auto"
                                                                                    maxMenuHeight={220}
                                                                                    styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                                                                    onChange={(opt) => setEditRow(emp.emp_id, type, idx, { component_id: opt ? opt.value : "" })}
                                                                                />
                                                                            </div>
                                                                            <button
                                                                                className="pg-pay-remove"
                                                                                title="Remove"
                                                                                onClick={() => removeEditRow(emp.emp_id, type, idx)}
                                                                            ><FaTrash /></button>
                                                                        </div>
                                                                        <div className="pg-pay-cell pg-pay-cell-amount">
                                                                            <input
                                                                                type="number"
                                                                                placeholder="0"
                                                                                min="0"
                                                                                value={row.amount}
                                                                                onWheel={(e) => e.target.blur()}
                                                                                onKeyDown={(e) => {
                                                                                    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                                                                                }}
                                                                                onPaste={(e) => {
                                                                                    const txt = (e.clipboardData || window.clipboardData).getData("text");
                                                                                    if (!/^\d*\.?\d*$/.test(txt)) e.preventDefault();
                                                                                }}
                                                                                onChange={(e) => {
                                                                                    let v = e.target.value;
                                                                                    if (v) {
                                                                                        v = v.replace(/^0+(?=[1-9])/, "");
                                                                                        const m = v.match(/^(\d*)(?:\.(\d{0,2}))?/);
                                                                                        if (m) v = m[1] + (m[2] !== undefined ? "." + m[2] : "");
                                                                                    }
                                                                                    setEditRow(emp.emp_id, type, idx, { amount: v });
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                );
                                                            };

                                                            return (
                                                                <div className="pg-pay-table" style={fontCssStyle(adj.font || emp.structure?.font)}>
                                                                    <div className="pg-pay-row pg-pay-head">
                                                                        <div className="pg-pay-cell">Earnings</div>
                                                                        <div className="pg-pay-cell pg-pay-right">Monthly in INR</div>
                                                                        <div className="pg-pay-cell">Deductions</div>
                                                                        <div className="pg-pay-cell pg-pay-right">Monthly in INR</div>
                                                                    </div>
                                                                    {Array.from({ length: maxStruct }).map((_, i) => (
                                                                        <div className="pg-pay-row" key={`prow-${i}`}>
                                                                            {renderEditCell("Earning", earnList, i)}
                                                                            {renderEditCell("Deduction", dedList, i)}
                                                                        </div>
                                                                    ))}
                                                                    <div className="pg-pay-row pg-pay-addrow">
                                                                        <div className="pg-pay-cell pg-pay-cell-add">
                                                                            <button onClick={() => addEditRow(emp.emp_id, "Earning")}><FaPlus /> Add earning</button>
                                                                        </div>
                                                                        <div className="pg-pay-cell" />
                                                                        <div className="pg-pay-cell pg-pay-cell-add">
                                                                            <button onClick={() => addEditRow(emp.emp_id, "Deduction")}><FaPlus /> Add deduction</button>
                                                                        </div>
                                                                        <div className="pg-pay-cell" />
                                                                    </div>
                                                                    {/* One-off addendum rows - read-only, distinct background
                                                                so admin sees what the top-section adjustments add. */}
                                                                    {maxAddon > 0 && Array.from({ length: maxAddon }).map((_, i) => {
                                                                        const e = addEarn[i];
                                                                        const d = addDed[i];
                                                                        return (
                                                                            <div className="pg-pay-row pg-pay-addon" key={`paddon-${i}`}>
                                                                                <div className="pg-pay-cell">{e ? e.label : ""}</div>
                                                                                <div className="pg-pay-cell pg-pay-right">{e ? fmt(e.amount) : ""}</div>
                                                                                <div className="pg-pay-cell">{d ? d.label : ""}</div>
                                                                                <div className="pg-pay-cell pg-pay-right">{d ? fmt(d.amount) : ""}</div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    <div className="pg-pay-row pg-pay-totals">
                                                                        <div className="pg-pay-cell">Total Earnings</div>
                                                                        <div className="pg-pay-cell pg-pay-right">{fmt(calc.gross)}</div>
                                                                        <div className="pg-pay-cell">Total Deductions</div>
                                                                        <div className="pg-pay-cell pg-pay-right">{fmt(calc.totalDed)}</div>
                                                                    </div>
                                                                    <div className="pg-pay-row pg-pay-netpay">
                                                                        <div className="pg-pay-cell" />
                                                                        <div className="pg-pay-cell" />
                                                                        <div className="pg-pay-cell">NET PAY</div>
                                                                        <div className="pg-pay-cell pg-pay-right pg-pay-netpay-val">₹{fmt(calc.netPay)}</div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Footer - Company Address override only.
                                                    The static Note + Authorised Signatory line are
                                                    rendered on the PDF/preview as hard-coded text; no
                                                    need to surface them here when they're not editable. */}
                                                        <div className="pg-adj-footer">
                                                            <div className="pg-adj-footer-address pg-adj-footer-address--full">
                                                                <label>Company Address</label>
                                                                <textarea
                                                                    rows={3}
                                                                    value={
                                                                        adj.company_address !== undefined
                                                                            ? adj.company_address
                                                                            : (emp.structure?.company_address || "")
                                                                    }
                                                                    onChange={(e) =>
                                                                        setAdj((p) => ({
                                                                            ...p,
                                                                            [emp.emp_id]: {
                                                                                ...(p[emp.emp_id] || {}),
                                                                                company_address: e.target.value,
                                                                            },
                                                                        }))
                                                                    }
                                                                    placeholder="e.g. DOLLUZ CORP, Floor 3, Tech Park, Chennai 600001"
                                                                />
                                                            </div>
                                                            {/* Per-payslip font override. Defaults to whatever the
                                                        salary structure already carries; admin can pick a
                                                        different preset for THIS month only. */}
                                                            <div className="pg-adj-footer-font">
                                                                <label>Payslip Font</label>
                                                                {(() => {
                                                                    const fontKey = adj.font || emp.structure?.font || DEFAULT_FONT_KEY;
                                                                    return (
                                                                        <select
                                                                            value={fontKey}
                                                                            onChange={(e) =>
                                                                                setAdj((p) => ({
                                                                                    ...p,
                                                                                    [emp.emp_id]: {
                                                                                        ...(p[emp.emp_id] || {}),
                                                                                        font: e.target.value,
                                                                                    },
                                                                                }))
                                                                            }
                                                                            style={fontCssStyle(fontKey)}
                                                                        >
                                                                            {PAYSLIP_FONTS.map((f) => (
                                                                                <option key={f.key} value={f.key} style={fontCssStyle(f.key)}>
                                                                                    {f.label}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>

                                    <button
                                        type="button"
                                        className="pg-adj-arrow pg-adj-arrow--next"
                                        onClick={() =>
                                            setCurrentEmpIndex((i) => Math.min(selectedEmps.length - 1, i + 1))
                                        }
                                        disabled={currentEmpIndex >= selectedEmps.length - 1}
                                        title="Next employee"
                                    >›</button>
                                </div>

                                {selectedEmps.length > 1 && (
                                    <div className="pg-adj-position">
                                        Showing <strong>{currentEmpIndex + 1}</strong> of <strong>{selectedEmps.length}</strong> employees
                                    </div>
                                )}

                                <div className="pg-step-footer">
                                    <button className="pg-btn-secondary" onClick={() => setStep(2)}>← Back</button>
                                    <button
                                        className="pg-btn-primary"
                                        onClick={() => {
                                            const v = validateAdjustments();
                                            if (v) {
                                                dnAlert.warning("Missing LOP Deduction", `${v.empName} - ${v.message}`);
                                                return;
                                            }
                                            setStep(4);
                                        }}
                                    >
                                        Review & Generate →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ══ STEP 4 - PREVIEW & GENERATE ══ */}
                        {step === 4 && !result && (
                            <div className="pg-step-panel">
                                <div className="pg-step-title">Review & Generate - {MONTH_NAMES[month]} {year}</div>
                                <p className="pg-step-desc">
                                    Review the summary below. Payslips will be created as <strong>Draft</strong>.
                                    You can review and publish them from Payslip Management.
                                </p>

                                {/* Batch preview table */}
                                <div className="pg-table-wrapper">
                                    <table className="pg-table">
                                        <thead>
                                            <tr>
                                                <th>Employee</th>
                                                <th>Department</th>
                                                <th>Gross</th>
                                                <th>Total Deductions</th>
                                                <th>Net Pay</th>
                                                <th>LOP Days</th>
                                                <th className="pg-num" style={{ textAlign: "center" }}>Preview</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedEmps.map(emp => {
                                                const calc = calcPayslip(emp.structure, adjustments[emp.emp_id] || {}, year, month, editItems[emp.emp_id]);
                                                return (
                                                    <tr key={emp.emp_id}>
                                                        <td>
                                                            <div className="pg-emp-cell">
                                                                <div className="pg-avatar pg-avatar--sm">{emp.emp_first_name?.[0]?.toUpperCase()}</div>
                                                                <div>
                                                                    <div className="pg-emp-name">{emp.emp_first_name} {emp.emp_last_name}</div>
                                                                    <div className="pg-emp-id">{emp.emp_id}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{emp.emp_department || "-"}</td>
                                                        <td className="pg-num">₹{fmt(calc.gross)}</td>
                                                        <td className="pg-num text-red">₹{fmt(calc.totalDed)}</td>
                                                        <td className="pg-num text-bold">₹{fmt(calc.netPay)}</td>
                                                        <td className="pg-num">{calc.lopDays > 0 ? calc.lopDays : "-"}</td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <button
                                                                type="button"
                                                                className="pg-preview-btn"
                                                                onClick={() => setPreviewEmpId(emp.emp_id)}
                                                                title="Preview payslip"
                                                            >
                                                                <FaEye /> Preview
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        {/* Totals row */}
                                        <tfoot>
                                            <tr className="pg-totals-row">
                                                <td colSpan={2}><strong>Total ({selectedEmps.length} employees)</strong></td>
                                                <td className="pg-num">
                                                    <strong>₹{fmt(selectedEmps.reduce((s, e) => s + calcPayslip(e.structure, adjustments[e.emp_id] || {}, year, month, editItems[e.emp_id]).gross, 0))}</strong>
                                                </td>
                                                <td className="pg-num text-red">
                                                    <strong>₹{fmt(selectedEmps.reduce((s, e) => s + calcPayslip(e.structure, adjustments[e.emp_id] || {}, year, month, editItems[e.emp_id]).totalDed, 0))}</strong>
                                                </td>
                                                <td className="pg-num text-bold">
                                                    <strong>₹{fmt(selectedEmps.reduce((s, e) => s + calcPayslip(e.structure, adjustments[e.emp_id] || {}, year, month, editItems[e.emp_id]).netPay, 0))}</strong>
                                                </td>
                                                <td></td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                <div className="pg-info-box" style={{ marginTop: 20 }}>
                                    <span className="pg-info-icon">ℹ</span>
                                    <strong>{selectedEmps.length}</strong> draft payslip{selectedEmps.length !== 1 ? "s" : ""} will be
                                    created for <strong>{MONTH_NAMES[month]} {year}</strong>.
                                    Batch number will be assigned automatically.
                                </div>

                                <div className="pg-step-footer">
                                    <button className="pg-btn-secondary" onClick={() => setStep(3)}>← Back</button>
                                    <button
                                        className="pg-btn-generate"
                                        onClick={handleGenerate}
                                        disabled={generating}
                                    >
                                        {generating ? "Generating…" : `⚡ Generate ${selectedEmps.length} Draft Payslip${selectedEmps.length !== 1 ? "s" : ""}`}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ══ SUCCESS STATE ══ */}
                        {step === 4 && result && (
                            <div className="pg-step-panel pg-success-panel">
                                <div className="pg-success-icon">✅</div>
                                <div className="pg-success-title">Payslips Created!</div>
                                <p className="pg-success-desc">
                                    <strong>{result.created_count}</strong> draft payslip{result.created_count !== 1 ? "s" : ""} created
                                    for <strong>{MONTH_NAMES[month]} {year}</strong>.
                                </p>
                                <div className="pg-success-batch">
                                    Batch: <span className="pg-batch-num">{result.batch_number}</span>
                                </div>
                                <p className="pg-success-hint">
                                    Go to <strong>Payslip Management</strong> to review and publish them.
                                </p>
                                <div className="pg-success-actions">
                                    <button className="pg-btn-secondary" onClick={handleStartNew}>
                                        Generate Another Month
                                    </button>
                                    <button className="pg-btn-primary" onClick={() => navigate("/Payslip_Management")}>
                                        Go to Management →
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* ══ PRE-GENERATION PAYSLIP PREVIEW ══
                Renders the exact PDF layout from the in-memory structure +
                adjustments, so admin can eyeball a payslip before creating
                the draft. Reuses the shared PayslipPreviewBody + pm-modal shell. */}
            {previewEmpId && (() => {
                const emp = selectedEmps.find(e => e.emp_id === previewEmpId);
                if (!emp) return null;
                const data = buildPreviewData(emp);
                return (
                    <>
                        <div className="pm-modal-overlay" onClick={() => setPreviewEmpId(null)} />
                        <div className="pm-modal pm-preview-modal">
                            <div className="pm-modal-header">
                                <div>
                                    <div className="pm-modal-title">Payslip preview</div>
                                    <div className="pm-modal-sub">
                                        {emp.emp_first_name} {emp.emp_last_name} &nbsp;·&nbsp; {MONTH_NAMES[month]} {year} &nbsp;·&nbsp; Draft (not yet generated)
                                    </div>
                                </div>
                                <button className="pm-modal-close" onClick={() => setPreviewEmpId(null)}><FaTimes /></button>
                            </div>
                            <div className="pm-modal-body pm-prev-pdf-body">
                                <PayslipPreviewBody data={data} />
                            </div>
                        </div>
                    </>
                );
            })()}
        </div>
    );
}

export default Payslip_Generate;